import { validators } from '@approvalmax/ui';
import { domain } from 'modules/data';
import { useCallback, useMemo } from 'react';
import bemFactory from 'react-bem-factory';

import {
    EmailData,
    EmailToSupplierChangeValidator,
    EmailToSupplierCreateValidator,
    EmailToSupplierFormProps,
} from './EmailToSupplierForm.types';

export const useEmailToSupplierFormQa = (integrationType: EmailToSupplierFormProps['integrationType']) => {
    return useMemo(() => {
        let qa;

        switch (integrationType) {
            case domain.IntegrationType.Xero:
                qa = bemFactory.qa('reqf-xero-email-to-supplier-section');
                break;

            case domain.IntegrationType.NetSuite:
                qa = bemFactory.qa('reqf-netsuite-email-to-supplier-section');
                break;

            case domain.IntegrationType.QBooks:
                qa = bemFactory.qa('reqf-qbooks-email-to-vendor-section');
                break;
        }

        return {
            qaForm: qa(),
            qaFormToggle: qa('send-to-supplier-checkbox'),
            qaTo: qa('to-editor'),
            qaCc: qa('cc-editor'),
            qaSubject: qa('subject-editor'),
            qaBody: qa('body-editor'),
        };
    }, [integrationType]);
};

export const useFormHandlers = (
    data: EmailToSupplierFormProps['data'],
    onChangeData: EmailToSupplierFormProps['onChangeData']
) => {
    const hasData = Boolean(data);

    const onChangeTo = useCallback(
        (value: EmailData[]) => {
            hasData &&
                onChangeData(
                    value.map((el) => el.id),
                    'to'
                );
        },
        [hasData, onChangeData]
    );

    const onChangeCc = useCallback(
        (value: EmailData[]) => {
            hasData &&
                onChangeData(
                    value.map((el) => el.id),
                    'cc'
                );
        },
        [hasData, onChangeData]
    );

    const onChangeSubject = useCallback(
        (value: string) => {
            hasData && onChangeData(value, 'subject');
        },
        [hasData, onChangeData]
    );

    const onChangeBody = useCallback(
        (value: string) => {
            hasData && onChangeData(value, 'body');
        },
        [hasData, onChangeData]
    );

    const onCreateEmail = useCallback((value: string): Promise<EmailData> => {
        return Promise.resolve({
            id: value,
            text: value,
        });
    }, []);

    return {
        onChangeTo,
        onChangeCc,
        onChangeSubject,
        onChangeBody,
        onCreateEmail,
    };
};

export const useValidations = (
    options: Pick<
        EmailToSupplierFormProps,
        'toValidatorFunction' | 'ccValidatorFunction' | 'toCreateValidatorFunction' | 'ccCreateValidatorFunction'
    >
) => {
    const { toValidatorFunction, ccValidatorFunction, toCreateValidatorFunction, ccCreateValidatorFunction } = options;

    const handleToValidate = useCallback<EmailToSupplierChangeValidator>(
        (data) => {
            return toValidatorFunction ? !toValidatorFunction(data) : true;
        },
        [toValidatorFunction]
    );

    const handleCcValidate = useCallback<EmailToSupplierChangeValidator>(
        (data) => {
            return ccValidatorFunction ? !ccValidatorFunction(data) : true;
        },
        [ccValidatorFunction]
    );

    const handleToCreateValidate = useCallback<EmailToSupplierCreateValidator>(
        (value, options) => {
            return toCreateValidatorFunction ? toCreateValidatorFunction(value, options) : validators.isEmail(value);
        },
        [toCreateValidatorFunction]
    );

    const handleCcCreateValidate = useCallback<EmailToSupplierCreateValidator>(
        (value, options) => {
            return ccCreateValidatorFunction ? ccCreateValidatorFunction(value, options) : validators.isEmail(value);
        },
        [ccCreateValidatorFunction]
    );

    return {
        handleToValidate,
        handleCcValidate,
        handleToCreateValidate,
        handleCcCreateValidate,
    };
};
